import { defineDocument, type LineGridDef } from './types.js'
import { ALL_TABS, BASIC_ACTIONS, col, f, opt } from './common.js'
import type { ColumnDef, FilterDef } from './types.js'

/**
 * docs/09 — tools are SERIALISED. Every line carries identifiers, because the
 * business tracks the individual drill, not a count of drills.
 */
const toolLines = (
  title: string, n: string, qtyLabel = 'Quantity', extra: LineGridDef['sources'] = [],
): LineGridDef => ({
  title, n,
  columns: [
    col.seq(), col.item('Tool'), col.desc(),
    { key: 'toolUnitId', label: 'Tool unit', type: 'text', width: 190, editable: true, lookup: { entity: 'tool_unit' },
      hint: 'The individual tool being moved. Its holder and condition follow this document.' },
    { key: 'identifiers', label: 'Identifier(s)', type: 'text', width: 180, editable: true },
    col.qty(qtyLabel), col.unit(),
  ],
  sources: [...extra, { key: 'excel', label: 'Import from Excel', source: 'excel' as const }],
})

const ENTRY_TYPES: ReadonlyArray<[string, string]> = [['ADD', 'Add'], ['REMOVE', 'Remove']]
const ISSUE_TYPES: ReadonlyArray<[string, string]> = [['ISSUE', 'Issue'], ['RETURN', 'Return']]
const options = (pairs: ReadonlyArray<[string, string]>) => pairs.map(([value, label]) => ({ value, label }))

/** Who holds a tool, and the warehouse it came from — as list columns. */
const warehouseColumn = { key: 'warehouseId', label: 'Warehouse', type: 'text' as const, width: 170 }
const createdOnColumn = { key: 'createdAt', label: 'Created on', type: 'date' as const, width: 130 }

/*
  STK-017, STK-018: the tool lists are the reference's own — Document no,
  Document date, then what the type is about, Status last among the filters.
  A tool document is not priced and an entry or issue is not a project's, so
  Project and Value are in the catalogue only.
*/
const docNoColumn: ColumnDef = { key: 'documentNo', label: 'Document no', type: 'docno', width: 152 }
const docDateColumn: ColumnDef = { key: 'documentDate', label: 'Document date', type: 'date', width: 128 }
const statusColumn: ColumnDef = { key: 'documentStatus', label: 'Status', type: 'status', width: 134 }
const catalogueOnly: ColumnDef[] = [opt('projectName', 'Project', 'text', 200), opt('total', 'Value', 'money', 150)]
const docNoFilter: FilterDef = { key: 'documentNo', label: 'Document no', control: 'text' }
const docDateFilter: FilterDef = { key: 'documentDate', label: 'Document date', control: 'dateRange' }
const statusFilter: FilterDef = { key: 'documentStatus', label: 'Status', control: 'status' }

export const toolEntry = defineDocument({
  key: 'tool_entry',
  group: 'Tools & Equipment',
  blurb: 'Add new tools to your tool inventory, or remove broken tools.',
  route: 'tool_entries',
  label: 'Tool entry',
  labelPlural: 'Tool entries',
  numbering: 'TOOL_ENTRY',
  numberPrefix: 'TE-',
  workflow: 'TOOL_ENTRY',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Tool entry',
    fields: [
      f.radio('entryType', 'Type', ENTRY_TYPES),
      f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('entryType', 'Type', ENTRY_TYPES),
          lockAfterDraft: true },
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        f.date('documentDate', 'Date', true),
        f.look('supplierId', 'Supplied by', 'supplier'),
        f.text('reason', 'Reason', 'full'),
        f.notes(),
      ] },
    ],
  },
  lines: toolLines('Tools', '02', 'Quantity'),
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'entryType', label: 'Type', type: 'text', width: 130 },
    statusColumn,
    warehouseColumn,
    createdOnColumn,
    ...catalogueOnly,
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'entryType', label: 'Type', control: 'select', options: options(ENTRY_TYPES) },
    statusFilter,
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: [],
} as const)

export const toolRequest = defineDocument({
  key: 'tool_request',
  group: 'Tools & Equipment',
  blurb: 'Use this for requesting tools for your projects or service work.',
  route: 'tool_reqs',
  label: 'Tool request',
  labelPlural: 'Tool requests',
  numbering: 'TOOL_REQUEST',
  numberPrefix: 'TR-',
  workflow: 'TOOL_REQUEST',
  tabs: ALL_TABS,
  /*
    STK-014: "Create Tool Request" opens the form itself, as the reference
    does — no setup modal. A wizard with no questions creates the draft
    straight away; the date and Issue to (the person raising it, TOOL-003)
    are filled by the create defaults, and everything else is asked on the
    form, where Project and the return date switch on with Request for.
  */
  createWizard: { title: 'Tool request', fields: [] },
  form: {
    sections: [
      // STK-015: the reference's order. Required by and Requested by are ours.
      { key: 'general', n: '01', title: 'General info', fields: [
        f.date('documentDate', 'Document date', true),
        { ...f.choice('requestFor', 'Request for', [['PROJECT', 'Project'], ['INDIVIDUAL_TOOLKIT', 'Individual Toolkit']]),
          defaultValue: 'PROJECT' },
        { ...f.look('projectId', 'Project', 'project', true),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
        // Tools for a project come back; a toolkit stays with the person.
        { ...f.date('returnBy', 'Return date (expected)', true),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] },
          hint: 'A toolkit stays with the person, so it has no return date.' },
        { ...f.look('issuedToId', 'Issue to', 'employee', true),
          hint: 'Starts on you. Change it when the tools are for somebody else.' },
        f.notes(),
        { ...f.date('requiredDate', 'Required by'),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
        f.look('requestedById', 'Requested by', 'employee'),
      ] },
    ],
  },
  lines: {
    title: 'Tools info', n: '02',
    columns: [
      col.seq(), col.item('Tool'), col.desc(), col.qty(), col.unit(),
      { key: 'issuedQty', label: 'Issued', type: 'qty', width: 100, align: 'right' },
      { key: 'openQty', label: 'Still to issue', type: 'qty', width: 128, align: 'right' },
      { key: 'preClosedQty', label: 'Pre closed', type: 'qty', width: 118, align: 'right' },
    ],
    sources: [{ key: 'excel', label: 'Import from Excel', source: 'excel' }],
  },
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'requestFor', label: 'Request for', type: 'text', width: 150 },
    { key: 'issuedToId', label: 'Issue to', type: 'text', width: 180 },
    statusColumn,
    { key: 'fulfilmentStatus', label: 'Issue status', type: 'text', width: 190 },
    createdOnColumn,
    ...catalogueOnly,
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'requestFor', label: 'Request for', control: 'select', options: options([['PROJECT', 'Project'], ['INDIVIDUAL_TOOLKIT', 'Individual Toolkit']]) },
    { key: 'issuedToId', label: 'Issue to', control: 'lookup', lookup: { entity: 'employee' } },
    statusFilter,
    { key: 'fulfilment', label: 'Issue status', control: 'select', options: [
      { value: 'OPEN', label: 'Still to issue' },
      { value: 'PARTIAL', label: 'Partly issued' },
      { value: 'COMPLETE', label: 'Fully issued' },
    ] },
  ],
  actions: [...BASIC_ACTIONS, 'preClose', 'undoPreClose'],
  rules: [],
  createsNext: [{ docType: 'tool_issue', label: 'Tool issue', whenStatus: 'APPROVED' }],
} as const)

export const toolIssue = defineDocument({
  key: 'tool_issue',
  group: 'Tools & Equipment',
  blurb: 'Use this for issuing tools against requests or for recording return of tools.',
  route: 'tool_issues',
  label: 'Tool issue',
  labelPlural: 'Tool issues',
  numbering: 'TOOL_ISSUE',
  numberPrefix: 'TI-',
  workflow: 'TOOL_ISSUE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Tool issue',
    subtitle: 'Issuing tools out, or recording them coming back.',
    fields: [
      // STK-016: Issue is the usual answer, so it starts chosen.
      { ...f.radio('issueType', 'Type', ISSUE_TYPES), defaultValue: 'ISSUE' },
      // TOOL-006: an issue answers a request, which already says who the
      // tools are for, the project and when they come back. Only requests
      // with tools still to issue are offered.
      { ...f.look('toolRequestId', 'Tool request', 'tool_request', true),
        visibleWhen: { field: 'issueType', equals: ['ISSUE'] } },
      // TOOL-007: a return is of what that person still holds.
      { ...f.look('issuedToId', 'Issue to (employee)', 'employee', true),
        visibleWhen: { field: 'issueType', equals: ['RETURN'] } },
      f.look('warehouseId', 'Warehouse', 'warehouse', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('issueType', 'Type', ISSUE_TYPES), lockAfterDraft: true, defaultValue: 'ISSUE' },
        { ...f.look('toolRequestId', 'Against request', 'tool_request'),
          visibleWhen: { field: 'issueType', equals: ['ISSUE'] } },
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        { ...f.look('issuedToId', 'Issued to', 'employee', true),
          hint: 'Taken from the request for an issue.' },
        f.look('projectId', 'Project', 'project'),
        f.date('documentDate', 'Date', true),
        f.date('returnBy', 'Due back'),
        // docs/09 — a fitter's own kit goes out with them and is not tracked
        // back to a warehouse shelf.
        f.yesNo('includePersonalToolkit', 'Includes their personal toolkit?'),
        f.notes(),
      ] },
    ],
  },
  lines: toolLines('Tools issued', '02', 'Issued', [
    { key: 'outstanding', label: 'Select the tools they hold', source: 'outstanding' },
  ]),
  columns: [
    docNoColumn,
    docDateColumn,
    { key: 'issueType', label: 'Type', type: 'text', width: 110 },
    { key: 'issuedToId', label: 'Issue to', type: 'text', width: 180 },
    statusColumn,
    { key: 'fulfilmentStatus', label: 'Return status', type: 'text', width: 190 },
    warehouseColumn,
    createdOnColumn,
    ...catalogueOnly,
  ],
  filters: [
    docNoFilter,
    docDateFilter,
    { key: 'issueType', label: 'Type', control: 'select', options: options(ISSUE_TYPES) },
    { key: 'issuedToId', label: 'Issue to', control: 'lookup', lookup: { entity: 'employee' } },
    statusFilter,
    { key: 'fulfilment', label: 'Return status', control: 'select', options: [
      { value: 'OPEN', label: 'Not fully returned' },
      { value: 'PARTIAL', label: 'Partly returned' },
      { value: 'COMPLETE', label: 'Fully returned' },
    ] },
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: [],
  allocatesFrom: ['tool_request'],
} as const)
